import { useCallback, useEffect, useMemo } from 'react';
import {
  LlmToolRequestActions,
  allSpecElementsBySlug,
  useFetchFsSpec,
  fullstorySelectors,
} from '@fullstory/redux';
import { useSelector } from 'react-redux';
import { RequestConfig, useAiLabelSuggestions } from '@fullstory/library-ui';

interface UseAiElementLabelSuggestionArgs {
  selector: string;
}

// TODO: currently we only support 1 selector
export const useAiElementLabelSuggestions = ({ selector }: UseAiElementLabelSuggestionArgs) => {
  useFetchFsSpec();
  const specElementsBySlug = useSelector(allSpecElementsBySlug);
  const orgId = useSelector(fullstorySelectors.getOrgId);

  const choices = useMemo(
    () => Object.values(specElementsBySlug).map(specElement => specElement.displayName),
    [specElementsBySlug],
  );

  const buildRequest = useCallback(
    (reqConfig: RequestConfig, blocklist: string[]) =>
      LlmToolRequestActions.suggestElementLabelSingle({
        orgId,
        selector,
        choices: reqConfig.choices,
        blocklist,
        useAdditionalContext: reqConfig.useAdditionalContext,
      }),
    [orgId, selector],
  );

  const requestConfigs: Array<{ choices: string[]; useAdditionalContext: boolean }> = useMemo(
    () => [
      // selector only
      {
        choices: [],
        useAdditionalContext: false,
      },
      // another selector to keep the choices rolling
      {
        choices: [],
        useAdditionalContext: false,
      },
      // spec
      {
        choices,
        useAdditionalContext: true,
      },

      // additional context
      {
        choices: [],
        useAdditionalContext: true,
      },
    ],
    [choices],
  );

  const getSpecSlug = useCallback(
    (name: string) => {
      const specElement = Object.values(specElementsBySlug).find(
        specElement => specElement.displayName === name,
      );
      return specElement ? specElement.slug : null;
    },
    [specElementsBySlug],
  );

  const { clearBlockList, ...aiLabelSuggestions } = useAiLabelSuggestions(
    buildRequest,
    getSpecSlug,
    requestConfigs,
  );

  useEffect(() => {
    clearBlockList();
  }, [selector, clearBlockList]);

  return { clearBlockList, ...aiLabelSuggestions };
};
